import React, { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import cn from 'clsx';
import { TFunction } from 'i18next';
import style from './style.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { GeneratedReport, GeneratedReportStatus } from '@interfaces/report-templates.interface';
import { convertDateTimeToMoment } from '@utils-clean/date-time-helpers';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { useMutationGeneratedReport } from '@apis/bff-api/report-templates';

type Props = {
    report: GeneratedReport;
    onClose: (success: boolean) => void;
};

type IdLabel = {
    id: GeneratedReportStatus;
    label: string;
};

type EditReportForm = {
    status: IdLabel | null;
    comments: string | null;
};

const statusDropdown = (t: TFunction) => [
    { id: GeneratedReportStatus.Ready, label: t('reports:Ready') },
    { id: GeneratedReportStatus.Obsolete, label: t('reports:Obsolete') },
];

const COMPONENT_NAME = 'edit-report-dialog';

export const EditReportDialog = ({ report, onClose }: Props) => {
    const { t } = useTranslation();
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();
    const { trigger: putGeneratedReport, isMutating } = useMutationGeneratedReport();

    const createdBy = t('common:createdUpdatedInfo', {
        date: stringDateWithoutTimeFromUtc(report.createdDate, 'DD-MMM-YYYY'),
        user: report.createdByName,
    });
    const updatedBy = t('common:createdUpdatedInfo', {
        date: stringDateWithoutTimeFromUtc(report.updatedDate, 'DD-MMM-YYYY'),
        user: report.updatedByName,
    });

    const statusDatasource = statusDropdown(t);

    const {
        control,
        handleSubmit,
        formState: { isDirty, isValid, isSubmitting },
        watch,
        setValue,
    } = useForm<EditReportForm>({
        defaultValues: {
            status: statusDatasource.find((d) => d.id === report.status),
            comments: report.comments,
        },
    });

    const watchStatus = watch('status') as IdLabel;

    useEffect(() => {
        if (watchStatus.id === GeneratedReportStatus.Ready) setValue('comments', '', { shouldDirty: true });
    }, [watchStatus]);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
    }, [isDirty]);

    useEffect(() => {
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, []);

    const submit: SubmitHandler<EditReportForm> = (data: EditReportForm) => {
        putGeneratedReport({
            ...report,
            status: data.status!.id,
            comments: data.comments,
        })
            .then(() => onClose(true))
            .catch((err) => window.console.log(err));
    };

    return (
        <DraggableDialog
            dataTestId="edit-report-dialog"
            title={t('reports:editReportModal', { id: report._itemId })}
            primaryBtnLabel={t('update')}
            primaryBtnDisabled={!isDirty || !isValid || isSubmitting || isMutating}
            onSubmit={() => handleSubmit(submit)()}
            secondaryBtnLabel={t('cancel')}
            onClose={async () => {
                if (componentHasChanges(COMPONENT_NAME)) {
                    const confirmed = await confirmDiscardChanges('edit-report-unsaved-dialog');
                    if (!confirmed) {
                        return;
                    }
                    setComponentHasChanges(COMPONENT_NAME, false);
                }
                onClose(false);
            }}
            initialHeight={610}
            draggable
            createdUpdatedInfo={<CreatedUpdatedInfo created={createdBy} updated={updatedBy} />}
            createdUpdatedBackgroundColour="gray"
            initialWidth={517}
        >
            <div className="k-h-full k-w-full k-d-flex-col k-gap-2">
                <div className={cn(style.dialogBanner, 'k-d-flex-row k-gap-3')}>
                    <div className="k-font-medium">{t('reports:generatedDate')}</div>
                    <div className={style.black50}>
                        {convertDateTimeToMoment(new Date(report.createdDate!), 'DD-MMM-yyyy HH:mm:ss UTC')}
                    </div>
                </div>
                <form id="edit-report-form" className="k-d-flex-col k-gap-2 k-flex-auto">
                    <BasicDropDownController
                        control={control}
                        name="status"
                        data-testid="edit-report-status"
                        data={statusDatasource}
                        dataItemKey="id"
                        textField="label"
                        label={`${t('ra:status')}*`}
                        style={{ width: '180px' }}
                    />
                    <TextAreaController
                        control={control}
                        name="comments"
                        data-testid="edit-report-comments"
                        maxLength={1200}
                        label={
                            watchStatus.id === GeneratedReportStatus.Obsolete
                                ? `${t('ra:comments')}*`
                                : t('ra:comments')
                        }
                        disabled={watchStatus.id === GeneratedReportStatus.Ready}
                        required={watchStatus.id === GeneratedReportStatus.Obsolete}
                        style={{ height: '350px', overflow: 'auto' }}
                    />
                </form>
            </div>
        </DraggableDialog>
    );
};
